import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { trackEvent } from "@/lib/pendo";

export interface GalleryItem {
  id: number;
  job_id?: number | null;
  scope?: "job" | "customer";
  customer_visible?: boolean;
  caption?: string | null;
  thumbnail?: { url: string };
  thumbnail_processed?: boolean;
  generating?: boolean;
  [key: string]: unknown;
}

export interface MediaUpdatePayload {
  id: number;
  job_id: number;
  thumbnail?: { url: string };
  thumbnail_processed?: boolean;
  file?: Record<string, unknown>;
  transcode_status?: string;
  [key: string]: unknown;
}

export interface UseGalleryActionsOptions<T extends GalleryItem> {
  items: () => T[];
  ownerId: () => number | null;
  // Dispatch payload key for the owner id — 'jobId' for job media, 'customerId'
  // for customer-level media. Defaults to 'jobId'.
  ownerParamKey?: string;
  deleteAction: string;
  visibilityAction: string;
  visibilityParamKey: string; // e.g., 'docId', 'photoId', 'videoId'
  captionAction?: string; // single-item caption update (photo/video only)
  // Realtime EventBus hooks are job-only (customer media carries no job_id, so
  // the broadcasts never match); customer scope omits both.
  thumbnailEventName?: string;
  createdEventName?: string;
  uploadCompleteEventName?: string;
  mediaType?: string;
}

export function useGalleryActions<T extends GalleryItem>(
  options: UseGalleryActionsOptions<T>
) {
  const store = useStore();
  const ownerParamKey = options.ownerParamKey ?? "jobId";

  // Delete state
  const deleteId = ref<number | null>(null);
  const deleteConfirm = ref(false);

  // Viewer state
  const viewerItem = ref<T | null>(null) as { value: T | null };
  const viewerOpenMode = ref<"read" | "edit">("read");

  function handleDelete(id: number) {
    deleteId.value = id;
    deleteConfirm.value = true;
  }

  function toggleDeleteConfirm() {
    deleteConfirm.value = !deleteConfirm.value;
  }

  function closeDeleteConfirm() {
    deleteConfirm.value = false;
    deleteId.value = null;
  }

  async function confirmDelete(): Promise<number | null> {
    const itemId = deleteId.value;
    if (itemId === null) return null;

    await store.dispatch(options.deleteAction, {
      [ownerParamKey]: options.ownerId(),
      id: itemId
    });

    if (options.mediaType === "video") {
      trackEvent("video_deleted", {
        video_id: itemId,
        job_id: options.ownerId()
      });
    }

    closeDeleteConfirm();
    return itemId;
  }

  function handleVisibilityToggle(item: T): Promise<void> {
    const { id, customer_visible } = item;
    const new_visibility = !customer_visible;

    const params: Record<string, unknown> = {
      [ownerParamKey]: options.ownerId(),
      [options.visibilityParamKey]: id,
      customer_visible: new_visibility
    };

    return store.dispatch(options.visibilityAction, params).then(() => {
      item.customer_visible = new_visibility;
      if (options.mediaType === "video") {
        trackEvent("video_visibility_toggled", {
          video_id: id,
          job_id: options.ownerId(),
          customer_visible: new_visibility
        });
      }
    });
  }

  function openViewer(item: T, mode: "read" | "edit" = "read") {
    if (item.generating) return;
    viewerItem.value = item;
    viewerOpenMode.value = mode;
  }

  function closeViewer() {
    viewerItem.value = null;
    viewerOpenMode.value = "read";
  }

  // Persist a caption on a single item, then reflect it locally so the card
  // badge and rail update without a refetch. Delete is a save of "".
  function saveCaption(item: T, caption: string): Promise<void> {
    if (!options.captionAction) return Promise.resolve();

    return store
      .dispatch(options.captionAction, {
        [ownerParamKey]: options.ownerId(),
        [options.visibilityParamKey]: item.id,
        caption
      })
      .then(() => {
        item.caption = caption;
        trackEvent("media_caption_edited", {
          media_type: options.mediaType,
          job_id: options.ownerId(),
          cleared: caption.length === 0
        });
      });
  }

  function deleteCaption(item: T): Promise<void> {
    return saveCaption(item, "");
  }

  // Navigation - computed index of current viewer item
  const viewerIndex = computed(() => {
    if (!viewerItem.value) return -1;
    return options.items().findIndex((i) => i.id === viewerItem.value?.id);
  });

  const canGoPrevious = computed(() => viewerIndex.value > 0);

  const canGoNext = computed(() => {
    const items = options.items();
    return viewerIndex.value >= 0 && viewerIndex.value < items.length - 1;
  });

  function goToPrevious() {
    if (!canGoPrevious.value) return;
    const items = options.items();
    viewerItem.value = items[viewerIndex.value - 1];
    viewerOpenMode.value = "read";
  }

  function goToNext() {
    if (!canGoNext.value) return;
    const items = options.items();
    viewerItem.value = items[viewerIndex.value + 1];
    viewerOpenMode.value = "read";
  }

  function handleMediaUpdate(payload: MediaUpdatePayload) {
    if (payload.job_id !== options.ownerId()) return;

    const item = options.items().find((i) => i.id === payload.id);
    if (!item) {
      if (options.createdEventName) {
        store.dispatch("fetchJob", { jobId: options.ownerId() });
      }
      return;
    }

    if (
      options.mediaType === "video" &&
      payload.transcode_status === "complete"
    ) {
      trackEvent("video_transcode_ready", {
        video_id: payload.id,
        job_id: payload.job_id
      });
    }

    // Merge all payload fields into the existing item, excluding identifiers
    const fields = { ...payload };
    delete fields.id;
    delete fields.job_id;
    Object.assign(item, fields);
  }

  function handleMediaCreate(payload: MediaUpdatePayload) {
    if (payload.job_id !== options.ownerId()) return;

    const items = options.items();

    const existing = items.find((i) => i.id === payload.id);
    if (existing) {
      handleMediaUpdate(payload);
      return;
    }

    const placeholderIndex = items.findIndex((i) => i.generating);
    if (placeholderIndex !== -1) {
      items.splice(placeholderIndex, 1);
    }

    items.push(payload as unknown as T);
  }

  // Lifecycle - register EventBus listeners (job scope only; see options)
  onMounted(() => {
    if (options.thumbnailEventName) {
      EventBus.$on(options.thumbnailEventName, handleMediaUpdate);
    }
    if (options.createdEventName) {
      EventBus.$on(options.createdEventName, handleMediaCreate);
    }
  });

  onBeforeUnmount(() => {
    if (options.thumbnailEventName) {
      EventBus.$off(options.thumbnailEventName, handleMediaUpdate);
    }
    if (options.createdEventName) {
      EventBus.$off(options.createdEventName, handleMediaCreate);
    }
  });

  return {
    // Delete state
    deleteId,
    deleteConfirm,
    handleDelete,
    toggleDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,

    // Visibility
    handleVisibilityToggle,

    // Viewer
    viewerItem,
    viewerOpenMode,
    openViewer,
    closeViewer,
    saveCaption,
    deleteCaption,

    // Navigation
    viewerIndex,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext
  };
}
